import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Trash2,
  Pencil,
  Loader2,
  Clock,
  Calendar as CalendarIcon,
  Repeat,
} from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import type {
  Appointment,
  AppointmentInput,
  AppointmentType,
  ReminderType,
  RecurrencePattern,
  RecurrenceEndMode,
  SeriesScope,
} from '../types';
import { RECURRENCE_LIMITS } from '../types';

interface AppointmentDetailModalProps {
  appointment: Appointment | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (
    id: string,
    input: Partial<AppointmentInput>,
    scope?: SeriesScope
  ) => Promise<boolean>;
  onDelete: (id: string, scope?: SeriesScope) => Promise<boolean>;
  /** Full appointments list (needed to compute affected-count in review step) */
  allAppointments: Appointment[];
}

const APPOINTMENT_TYPES: AppointmentType[] = ['doctor', 'checkup', 'ultrasound', 'course', 'personal'];
const REMINDER_OPTIONS: ReminderType[] = ['none', '1_day', '1_week'];
const PATTERNS: RecurrencePattern[] = ['weekly', 'biweekly', 'monthly'];

type ModalStep = 'view' | 'scopeSelect' | 'edit' | 'reviewEdit' | 'reviewDelete';
type Intent = 'edit' | 'delete';

export function AppointmentDetailModal({
  appointment,
  isOpen,
  onClose,
  onUpdate,
  onDelete,
  allAppointments,
}: AppointmentDetailModalProps) {
  const { t, i18n } = useTranslation('calendar');

  const [step, setStep] = useState<ModalStep>('view');
  const [intent, setIntent] = useState<Intent | null>(null);
  const [scope, setScope] = useState<SeriesScope>('single');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Edit state
  const [title, setTitle] = useState(appointment?.title || '');
  const [description, setDescription] = useState(appointment?.description || '');
  const [date, setDate] = useState(appointment?.appointment_date || '');
  const [time, setTime] = useState(appointment?.appointment_time?.slice(0, 5) || '10:00');
  const [isAllDay, setIsAllDay] = useState(appointment?.is_all_day || false);
  const [appointmentType, setAppointmentType] = useState<AppointmentType>(
    appointment?.appointment_type || 'personal'
  );
  const [reminder, setReminder] = useState<ReminderType>(
    appointment?.reminder || 'none'
  );

  // Series-edit state (only used when scope !== 'single')
  const [pattern, setPattern] = useState<RecurrencePattern>(
    appointment?.recurrence_pattern || 'weekly'
  );
  const [patternChanged, setPatternChanged] = useState(false);
  const [endMode, setEndMode] = useState<RecurrenceEndMode>('count');
  const [endDate, setEndDate] = useState('');
  const [count, setCount] = useState<number>(4);
  const [recurrenceError, setRecurrenceError] = useState<string | null>(null);

  const isSeries = !!appointment?.recurrence_group_id;

  const affectedCount = useMemo(() => {
    if (!appointment) return 1;
    if (!isSeries || scope === 'single') return 1;
    const group = allAppointments.filter(
      (a) => a.recurrence_group_id === appointment.recurrence_group_id
    );
    if (scope === 'all') return group.length;
    return group.filter(
      (a) => a.appointment_date >= appointment.appointment_date
    ).length;
  }, [appointment, isSeries, scope, allAppointments]);

  const resetState = () => {
    setStep('view');
    setIntent(null);
    setScope('single');
    setPatternChanged(false);
    setEndMode('count');
    setEndDate('');
    setCount(4);
    setRecurrenceError(null);
  };

  if (!isOpen || !appointment) return null;

  const handleBackdrop = () => {
    resetState();
    onClose();
  };

  const handleStartIntent = (next: Intent) => {
    setIntent(next);
    if (isSeries) {
      setScope('single');
      setStep('scopeSelect');
    } else {
      if (next === 'edit') {
        setTitle(appointment.title);
        setDescription(appointment.description || '');
        setDate(appointment.appointment_date);
        setTime(appointment.appointment_time?.slice(0, 5) || '10:00');
        setIsAllDay(appointment.is_all_day);
        setAppointmentType(appointment.appointment_type);
        setReminder(appointment.reminder);
        setStep('edit');
      } else {
        setStep('reviewDelete');
      }
    }
  };

  const handleScopeNext = () => {
    if (intent === 'edit') {
      setTitle(appointment.title);
      setDescription(appointment.description || '');
      setDate(appointment.appointment_date);
      setTime(appointment.appointment_time?.slice(0, 5) || '10:00');
      setIsAllDay(appointment.is_all_day);
      setAppointmentType(appointment.appointment_type);
      setReminder(appointment.reminder);
      setPattern(appointment.recurrence_pattern || 'weekly');
      setPatternChanged(false);
      setStep('edit');
    } else {
      setStep('reviewDelete');
    }
  };

  const validateSeriesRecurrence = (): string | null => {
    if (scope === 'single' || !patternChanged) return null;
    const start = new Date(date);
    const cap = RECURRENCE_LIMITS[pattern];
    if (endMode === 'until') {
      if (!endDate) return t('recurring.errors.endBeforeStart');
      const end = new Date(endDate);
      if (end < start) return t('recurring.errors.endBeforeStart');
      const maxEnd = new Date(start);
      maxEnd.setFullYear(maxEnd.getFullYear() + 1);
      if (end > maxEnd) return t('recurring.errors.maxOneYear');
    } else {
      if (!Number.isFinite(count) || count < 1 || count > cap) {
        return t('recurring.errors.countRange', { max: cap });
      }
    }
    return null;
  };

  const handleEditNext = () => {
    if (!title.trim() || !date) return;
    const recErr = validateSeriesRecurrence();
    if (recErr) {
      setRecurrenceError(recErr);
      return;
    }
    setRecurrenceError(null);
    setStep('reviewEdit');
  };

  const handleSaveConfirm = async () => {
    setIsSaving(true);
    const input: Partial<AppointmentInput> = {
      title: title.trim(),
      description: description.trim() || undefined,
      appointment_date: date,
      appointment_time: isAllDay ? undefined : time,
      is_all_day: isAllDay,
      appointment_type: appointmentType,
      reminder,
    };
    if (isSeries && scope !== 'single' && patternChanged) {
      input.recurrence = {
        pattern,
        endMode,
        endDate: endMode === 'until' ? endDate : undefined,
        count: endMode === 'count' ? count : undefined,
      };
    }
    const success = await onUpdate(appointment.id, input, scope);
    setIsSaving(false);
    if (success) {
      resetState();
      onClose();
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    const success = await onDelete(appointment.id, isSeries ? scope : 'single');
    setIsDeleting(false);
    if (success) {
      resetState();
      onClose();
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'de-DE', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatTime = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':');
    const suffix = t('card.timeSuffix');
    return suffix ? `${hours}:${minutes} ${suffix}` : `${hours}:${minutes}`;
  };

  // ---------- Step: review delete ----------
  if (step === 'reviewDelete') {
    const content = (
      <>
        <div className="bottom-sheet-backdrop" onClick={() => setStep(isSeries ? 'scopeSelect' : 'view')} />
        <div className="bottom-sheet-container">
          <div className="bottom-sheet-modal">
            <div className="bottom-sheet-header">
              <h2>{t('detailModal.reviewDelete.title')}</h2>
              <button
                onClick={handleBackdrop}
                className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="bottom-sheet-content">
              <p className="text-sm text-white/70 mb-2">
                {isSeries
                  ? t('detailModal.reviewDelete.summary', { count: affectedCount })
                  : t('detailModal.deleteDescription')}
              </p>
              <div className="mt-3 text-white font-medium">
                {appointment.title}
              </div>
            </div>
            <div className="bottom-sheet-footer">
              <button
                className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
                onClick={() => setStep(isSeries ? 'scopeSelect' : 'view')}
                disabled={isDeleting}
              >
                {t('detailModal.cancel')}
              </button>
              <button
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-medium flex items-center justify-center"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('detailModal.delete')}
              </button>
            </div>
          </div>
        </div>
      </>
    );
    return createPortal(content, document.body);
  }

  // ---------- Step: review edit ----------
  if (step === 'reviewEdit') {
    const content = (
      <>
        <div className="bottom-sheet-backdrop" onClick={() => setStep('edit')} />
        <div className="bottom-sheet-container">
          <div className="bottom-sheet-modal">
            <div className="bottom-sheet-header">
              <h2>{t('detailModal.reviewEdit.title')}</h2>
              <button
                onClick={handleBackdrop}
                className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="bottom-sheet-content space-y-3">
              <p className="text-sm text-white/70">
                {isSeries
                  ? t('detailModal.reviewEdit.summary', { count: affectedCount })
                  : t('detailModal.reviewEdit.singleSummary')}
              </p>
              <div className="glass-effect rounded-xl p-3 space-y-1">
                <div className="text-white font-medium">{title}</div>
                <div className="text-sm text-white/60">{formatDate(date)}</div>
                {!isAllDay && (
                  <div className="text-sm text-white/60">{formatTime(time)}</div>
                )}
                <div className="text-xs text-white/50">
                  {t(`appointmentTypes.${appointmentType}`)}
                </div>
              </div>
            </div>
            <div className="bottom-sheet-footer">
              <button
                className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
                onClick={() => setStep('edit')}
                disabled={isSaving}
              >
                {t('detailModal.cancel')}
              </button>
              <button
                className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium flex items-center justify-center hover:bg-pp-orange/90"
                onClick={handleSaveConfirm}
                disabled={isSaving}
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('detailModal.confirm')}
              </button>
            </div>
          </div>
        </div>
      </>
    );
    return createPortal(content, document.body);
  }

  // ---------- Step: scope select ----------
  if (step === 'scopeSelect') {
    const options: SeriesScope[] = ['single', 'following', 'all'];
    const content = (
      <>
        <div className="bottom-sheet-backdrop" onClick={() => setStep('view')} />
        <div className="bottom-sheet-container">
          <div className="bottom-sheet-modal">
            <div className="bottom-sheet-header">
              <h2>{t('detailModal.scope.title')}</h2>
              <button
                onClick={handleBackdrop}
                className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="bottom-sheet-content space-y-2">
              {options.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setScope(opt)}
                  className={`w-full text-left px-3 py-3 rounded-xl border transition-colors ${
                    scope === opt
                      ? 'bg-pp-teal text-white border-white/25'
                      : 'glass-effect text-white/80 border-white/25 hover:bg-white/10'
                  }`}
                >
                  {t(`detailModal.scope.${opt}`)}
                </button>
              ))}
            </div>
            <div className="bottom-sheet-footer">
              <button
                className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
                onClick={() => setStep('view')}
              >
                {t('detailModal.cancel')}
              </button>
              <button
                className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium"
                onClick={handleScopeNext}
              >
                {t('detailModal.continue')}
              </button>
            </div>
          </div>
        </div>
      </>
    );
    return createPortal(content, document.body);
  }

  // ---------- Step: edit ----------
  if (step === 'edit') {
    const showSeriesFields = isSeries && scope !== 'single';
    const content = (
      <>
        <div className="bottom-sheet-backdrop" onClick={() => setStep(isSeries ? 'scopeSelect' : 'view')} />
        <div className="bottom-sheet-container">
          <div className="bottom-sheet-modal">
            <div className="bottom-sheet-header">
              <h2>{t('detailModal.editTitle')}</h2>
              <button
                onClick={handleBackdrop}
                className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bottom-sheet-content space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">{t('detailModal.titleLabel')}</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">{t('detailModal.dateLabel')}</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-pp-orange"
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="editIsAllDay"
                  checked={isAllDay}
                  onChange={(e) => setIsAllDay(e.target.checked)}
                  className="w-5 h-5 rounded border-white/25 bg-white/10 text-pp-orange"
                />
                <label htmlFor="editIsAllDay" className="text-sm text-white/70">{t('detailModal.allDay')}</label>
              </div>

              {!isAllDay && (
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">{t('detailModal.timeLabel')}</label>
                  <input
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-pp-orange"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">{t('detailModal.typeLabel')}</label>
                <div className="flex flex-wrap gap-2">
                  {APPOINTMENT_TYPES.map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setAppointmentType(type)}
                      className={`px-3 py-1.5 rounded-[5px] text-sm border ${
                        appointmentType === type
                          ? 'bg-pp-teal text-white border-white/25'
                          : 'glass-effect text-white/70 border-white/25 hover:bg-white/10'
                      }`}
                    >
                      {t(`appointmentTypes.${type}`)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">{t('detailModal.reminderLabel')}</label>
                <select
                  value={reminder}
                  onChange={(e) => setReminder(e.target.value as ReminderType)}
                  className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-pp-orange"
                >
                  {REMINDER_OPTIONS.map((option) => (
                    <option key={option} value={option} className="bg-pp-dark text-white">{t(`reminderTypes.${option}`)}</option>
                  ))}
                </select>
              </div>

              {showSeriesFields && (
                <>
                  <div className="flex items-center gap-3 pt-2 border-t border-white/10">
                    <input
                      type="checkbox"
                      id="editPatternChanged"
                      checked={patternChanged}
                      onChange={(e) => setPatternChanged(e.target.checked)}
                      className="w-5 h-5 rounded border-white/25 bg-white/10 text-pp-orange"
                    />
                    <label htmlFor="editPatternChanged" className="text-sm text-white/70">
                      {t('detailModal.changeRecurrence')}
                    </label>
                  </div>

                  {patternChanged && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-white/70 mb-2">{t('recurring.patternLabel')}</label>
                        <div className="flex flex-wrap gap-2">
                          {PATTERNS.map((p) => (
                            <button
                              key={p}
                              type="button"
                              onClick={() => setPattern(p)}
                              className={`px-3 py-1.5 rounded-[5px] text-sm border ${
                                pattern === p
                                  ? 'bg-pp-teal text-white border-white/25'
                                  : 'glass-effect text-white/70 border-white/25 hover:bg-white/10'
                              }`}
                            >
                              {t(`recurring.patterns.${p}`)}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white/70 mb-2">{t('recurring.endModeLabel')}</label>
                        <div className="flex gap-2">
                          {(['count', 'until'] as RecurrenceEndMode[]).map((m) => (
                            <button
                              key={m}
                              type="button"
                              onClick={() => setEndMode(m)}
                              className={`px-3 py-1.5 rounded-[5px] text-sm border ${
                                endMode === m
                                  ? 'bg-pp-teal text-white border-white/25'
                                  : 'glass-effect text-white/70 border-white/25 hover:bg-white/10'
                              }`}
                            >
                              {t(`recurring.endMode.${m}`)}
                            </button>
                          ))}
                        </div>
                      </div>

                      {endMode === 'until' ? (
                        <div>
                          <label className="block text-sm font-medium text-white/70 mb-1">{t('recurring.endDateLabel')}</label>
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-pp-orange"
                          />
                        </div>
                      ) : (
                        <div>
                          <label className="block text-sm font-medium text-white/70 mb-1">
                            {scope === 'following'
                              ? t('recurring.countLabelFollowing')
                              : t('recurring.countLabel')}
                          </label>
                          <input
                            type="number"
                            min={1}
                            max={RECURRENCE_LIMITS[pattern]}
                            value={count}
                            onChange={(e) => setCount(parseInt(e.target.value || '0', 10))}
                            className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-pp-orange"
                          />
                        </div>
                      )}

                      {recurrenceError && (
                        <p className="text-red-400 text-xs">{recurrenceError}</p>
                      )}
                    </>
                  )}
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">{t('detailModal.notesLabel')}</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 resize-none focus:outline-none focus:ring-2 focus:ring-pp-orange"
                />
              </div>
            </div>

            <div className="bottom-sheet-footer">
              <button
                className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
                onClick={() => setStep(isSeries ? 'scopeSelect' : 'view')}
              >
                {t('detailModal.cancel')}
              </button>
              <button
                className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium flex items-center justify-center hover:bg-pp-orange/90"
                onClick={handleEditNext}
                disabled={!title.trim() || !date}
              >
                {t('detailModal.continue')}
              </button>
            </div>
          </div>
        </div>
      </>
    );
    return createPortal(content, document.body);
  }

  // ---------- Step: view ----------
  const viewContent = (
    <>
      <div className="bottom-sheet-backdrop" onClick={handleBackdrop} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">
          <div className="bottom-sheet-header">
            <h2>{t('detailModal.detailTitle')}</h2>
            <button
              onClick={handleBackdrop}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="bottom-sheet-content space-y-4">
            <div>
              <h3 className="text-xl font-bold text-white">{appointment.title}</h3>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className="inline-block px-2 py-1 rounded-[5px] text-xs font-medium bg-pp-teal text-white border border-white/25">
                  {t(`appointmentTypes.${appointment.appointment_type}`)}
                </span>
                {appointment.recurrence_group_id && appointment.recurrence_pattern && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-[5px] text-xs font-medium bg-white/10 text-white border border-white/25">
                    <Repeat className="w-3 h-3" />
                    {t(`recurring.patterns.${appointment.recurrence_pattern}`)}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2 text-white/70">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-white/50" />
                <span>{formatDate(appointment.appointment_date)}</span>
              </div>

              {!appointment.is_all_day && appointment.appointment_time && (
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-white/50" />
                  <span>{formatTime(appointment.appointment_time)}</span>
                </div>
              )}

              {appointment.is_all_day && (
                <span className="text-sm text-white/50 ml-7">{t('detailModal.allDay')}</span>
              )}
            </div>

            {appointment.description && (
              <div className="pt-2 border-t border-white/10">
                <p className="text-sm text-white/70">{appointment.description}</p>
              </div>
            )}

            {appointment.is_predefined && appointment.ssw_week && (
              <div className="text-sm text-pp-orange font-medium">
                {t('detailModal.sswRecommendation', { week: appointment.ssw_week })}
              </div>
            )}
          </div>

          <div className="bottom-sheet-footer">
            <button
              className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium flex items-center justify-center gap-2 hover:bg-white/30"
              onClick={() => handleStartIntent('delete')}
            >
              <Trash2 className="w-4 h-4" />
              {t('detailModal.delete')}
            </button>
            <button
              className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium flex items-center justify-center gap-2 hover:bg-pp-orange/90"
              onClick={() => handleStartIntent('edit')}
            >
              <Pencil className="w-4 h-4" />
              {t('detailModal.edit')}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(viewContent, document.body);
}
